/**
 * The ONE Firebase bootstrap module (plain Firebase JS SDK - no AngularFire).
 *
 * - Firestore is initialised with `persistentLocalCache` - this IS the
 *   offline cache the spec's client-sync.md relies on.
 * - Functions is pinned to `environment.region` (us-east4), the same
 *   single-place policy as the backend's `setGlobalOptions`. Calling any
 *   other region 404s.
 * - When `environment.useEmulators`, each service connects to its emulator
 *   exactly once, on first use.
 *
 * Import the getters (`db()`, `auth()`, `functions()`) - never
 * `initializeApp` or the `firebase/*` init functions - anywhere else.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import {
  clearIndexedDbPersistence,
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  terminate,
  type Firestore,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';

import { environment } from '../../environments/environment';

let cachedApp: FirebaseApp | undefined;
let cachedDb: Firestore | undefined;
let cachedAuth: Auth | undefined;
let cachedFunctions: Functions | undefined;

function app(): FirebaseApp {
  return (cachedApp ??= initializeApp(environment.firebase));
}

/** Firestore with the persistent local cache (read-only from this app). */
export function db(): Firestore {
  if (!cachedDb) {
    cachedDb = initializeFirestore(app(), {
      // Multi-tab on purpose: the DEFAULT tab manager gives the cache to the
      // first tab and silently drops every later tab to a MEMORY cache - so a
      // second console tab re-read whole collections from the server on every
      // load (thousands of billed reads once accounts scale). With the
      // multi-tab manager the tabs share one IndexedDB cache behind a leader
      // election: one primary owns the network (the server sees ONE listener
      // per query regardless of tab count) and hands over, resume tokens
      // intact, if its tab closes.
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
    if (environment.useEmulators) {
      const [host, port] = environment.emulators.firestore.split(':');
      connectFirestoreEmulator(cachedDb, host ?? '127.0.0.1', Number(port ?? 8080));
    }
  }
  return cachedDb;
}

/**
 * Wipe the on-disk Firestore cache. Call on SIGN-OUT.
 *
 * `persistentLocalCache()` is what makes the console usable offline, and it is
 * also a copy of every partner, account and invite the console ever loaded,
 * sitting in the browser profile. Signing out cleared the auth signals and
 * detached the listeners, and left all of it there - so on a shared machine, or
 * a laptop going back to IT, the next person's devtools had the customer base
 * in IndexedDB.
 *
 * Order is fixed by the SDK: `clearIndexedDbPersistence` refuses to run against
 * a live client, so the instance must be terminated first, and a terminated
 * instance cannot be reused. Dropping `cachedDb` means the next `db()` builds a
 * fresh one - safe because every call site calls `db()` at point of use rather
 * than holding the handle.
 *
 * Best-effort by design. This runs while someone is signing out; if the clear
 * fails (another tab still has the database open is the common one) that must
 * not strand them in a half-signed-out console. It reports and moves on.
 *
 * ACCEPTED TRADE under the multi-tab cache: with a second tab open, the SDK
 * refuses the clear (failed-precondition), so the on-disk copy survives until
 * the LAST tab signs out or closes. Auth state is shared, so the other tab is
 * already signed out and detached - nothing keeps FLOWING; the residue is the
 * cache itself, cleared on the next single-tab sign-out. If the wipe ever
 * needs to be absolute, the upgrade path is a BroadcastChannel round asking
 * other tabs to terminate their instances first.
 */
export async function clearFirestoreCache(): Promise<void> {
  const instance = cachedDb;
  if (!instance) return;
  cachedDb = undefined;
  try {
    await terminate(instance);
    await clearIndexedDbPersistence(instance);
  } catch (error) {
    console.warn(
      '[firebase] could not clear the offline cache on sign-out. Cached console ' +
        'data may remain in this browser profile.',
      error,
    );
  }
}

/**
 * Firebase Auth (emulator-backed in the dev loop). Explicit persistence via
 * initializeAuth, never the getter with default wiring: the default's
 * popup/redirect resolver never resolves the first onAuthStateChanged under
 * capacitor:// on WKWebView, which wedges every auth-gated route on a native
 * build. Web behaviour is identical.
 */
export function auth(): Auth {
  if (!cachedAuth) {
    cachedAuth = initializeAuth(app(), {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
    });
    if (environment.useEmulators) {
      connectAuthEmulator(cachedAuth, environment.emulators.auth, {
        disableWarnings: true,
      });
    }
  }
  return cachedAuth;
}

/**
 * Cloud Functions, region-pinned - every mutation goes through a Callable
 * on this instance (zero client writes). In the dev loop the blume-functions
 * repo's `npm run serve` runs the Functions emulator on port 5001, so
 * Callables work fully locally.
 */
export function functions(): Functions {
  if (!cachedFunctions) {
    cachedFunctions = getFunctions(app(), environment.region);
    if (environment.useEmulators) {
      const [host, port] = environment.emulators.functions.split(':');
      connectFunctionsEmulator(cachedFunctions, host ?? '127.0.0.1', Number(port ?? 5001));
    }
  }
  return cachedFunctions;
}
